import type { WorkspaceMenuItemResponse } from '@protoss/shared';
import {
  BarChartIcon,
  DashboardIcon,
  GearIcon,
  IconSize,
  PersonIcon,
  Share1Icon,
  SkeletonBlock,
  TokensIcon,
} from '@protoss/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { StageBadge } from '../lib/featureStage';
import './administration-page.css';

// Maps iconKey strings to rendered icon nodes at IconSize.sm for the admin context pane.
const ADMIN_ICON_MAP: Record<string, React.ReactNode> = {
  dashboard: <DashboardIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />,
  'share-1': <Share1Icon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />,
  person: <PersonIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />,
  gear: <GearIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />,
  cube: <TokensIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />,
  'bar-chart': <BarChartIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />,
};

export function resolveAdminMenuIcon(iconKey: string): React.ReactNode {
  return (
    ADMIN_ICON_MAP[iconKey] ?? (
      <GearIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
    )
  );
}

export interface AdminContextMenuPaneProps {
  /** routeSegment of the currently active admin section (e.g. "products") */
  selectedSection: string;
  /** Whether the menu is in icon-only collapsed mode */
  collapsed: boolean;
  /** Toggle collapse state */
  onToggle: () => void;
  /** Menu items from useWorkspaceMenu() in the parent */
  menuItems: WorkspaceMenuItemResponse[];
  /** Loading state from useWorkspaceMenu() in the parent */
  isMenuLoading: boolean;
}

/**
 * AdminContextMenuPane - reusable admin navigation menu rendered in the context pane.
 * Used by both AdministrationPage and ProductAdminPage so both surfaces share the
 * same admin context pane while the page manages its own collapse state.
 */
export function AdminContextMenuPane({
  selectedSection,
  collapsed,
  onToggle,
  menuItems,
  isMenuLoading,
}: AdminContextMenuPaneProps) {
  const enabledMenuItems = menuItems.filter((item) => item.isEnabled);
  return (
    <div className={`admin-menu${collapsed ? ' admin-menu--collapsed' : ''}`}>
      <div className="admin-menu-header">
        {!collapsed && <h3 className="admin-menu-title">Administration</h3>}
        <button
          type="button"
          className="admin-menu-header-toggle"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand administration menu' : 'Collapse administration menu'}
          aria-expanded={!collapsed}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            {collapsed ? (
              <>
                <path
                  d="M4 12l4-4-4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 12l4-4-4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </>
            ) : (
              <>
                <path
                  d="M12 12l-4-4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 12l-4-4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </>
            )}
          </svg>
        </button>
      </div>
      <nav
        className="admin-menu-nav"
        aria-label="Administration menu"
        aria-busy={isMenuLoading || undefined}
      >
        {isMenuLoading ? (
          <>
            <div className="admin-menu-item-skeleton" aria-hidden="true">
              <SkeletonBlock />
            </div>
            <div className="admin-menu-item-skeleton" aria-hidden="true">
              <SkeletonBlock />
            </div>
            <div className="admin-menu-item-skeleton" aria-hidden="true">
              <SkeletonBlock />
            </div>
          </>
        ) : (
          enabledMenuItems.map((item) => (
            <Link
              key={item.key}
              to={`/administration/${item.routeSegment}`}
              className="admin-menu-item"
              data-selected={selectedSection === item.routeSegment || undefined}
            >
              <span className="admin-menu-item-icon" aria-hidden="true">
                {resolveAdminMenuIcon(item.iconKey)}
              </span>
              <span className="admin-menu-item-label">{item.label}</span>
              {!collapsed && <StageBadge stage={item.stage} />}
            </Link>
          ))
        )}
      </nav>
    </div>
  );
}
